import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TablePagination,
    Menu,
    MenuItem,
    ListItemIcon as MuiListItemIcon,
    Breadcrumbs,
    Link,
} from '@mui/material';
import { Add, Refresh, Edit, Delete, Visibility, VisibilityOff, MoreVert, ArrowBack, AttachMoney, Inventory, NotificationsActive } from '@mui/icons-material';
import { getProductById } from '../../services/Api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllVarients = () => {
    const { subCategoryId, productId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    const productName = location.state?.productName || 'Product';
    const subCategoryName = location.state?.subCategoryName || 'Subcategory';
    const categoryName = location.state?.categoryName || 'Category';
    const categoryId = location.state?.categoryId;

    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state (client-side for now since variants are nested)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedVariant, setMenuSelectedVariant] = useState(null);

    // Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);

    const fetchVariants = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getProductById(productId);
            if (response.success) {
                // Ensure we handle the data structure correctly
                // Response format based on user's curl matches property names exactly.
                setVariants(response.data?.variants || []);
            } else {
                setError(response?.message || 'Failed to fetch variants');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching variants');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    const handleMenuOpen = (event, variant) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedVariant(variant);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSelectedVariant(null);
    };

    const handleDeleteClick = () => {
        setSelectedVariant(menuSelectedVariant);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirmDelete = async () => {
        console.error('Delete variant functionality needs to be confirmed with backend structure.');
        // TODO: Implement delete functionality once backend structure is confirmed
        handleMenuClose();
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/categories');
                        }}
                    >
                        Categories
                    </Link>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            if (categoryId) {
                                navigate(`/categories/${categoryId}/items`, { state: { categoryName } });
                            } else {
                                navigate('/categories');
                            }
                        }}
                    >
                        {categoryName}
                    </Link>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(`/sub-categories/${subCategoryId}/products`, { state: { subCategoryName, categoryName, categoryId } });
                        }}
                    >
                        {subCategoryName} Products
                    </Link>
                    <Typography color="text.primary">{productName} Variants</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton 
                            onClick={() => navigate(`/sub-categories/${subCategoryId}/products`, { state: { subCategoryName, categoryName, categoryId } })}
                            sx={{ border: '1px solid', borderColor: 'text.border' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 300 }}>
                                {productName} Variants
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage pricing and stock levels for each variant
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <IconButton onClick={fetchVariants} disabled={loading} sx={{ color: 'primary.main' }}>
                            <Refresh />
                        </IconButton>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate(`/variants/modify/${productId}`, { 
                                state: { productName, subCategoryName, categoryName, categoryId, subCategoryId } 
                            })}
                            sx={{ px: 4, backgroundColor: 'primary.main' }}
                        >
                            Create Variant
                        </Button>
                    </Box>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 800 }} aria-label="variants table">
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Variant Label</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Discount Price</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Stock</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Low Stock Alert</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : variants.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No variants found for this product</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                variants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((variant) => (
                                    <TableRow key={variant.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell sx={{ fontWeight: 600 }}>{variant.variantLabel}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                {variant.price}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', color: variant.discountPrice < variant.price ? 'error.main' : 'inherit' }}>
                                                <AttachMoney sx={{ fontSize: 16 }} />
                                                {variant.discountPrice}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={variant.stockQuantity || 0} 
                                                size="small" 
                                                variant="outlined"
                                                icon={<Inventory sx={{ fontSize: '14px !important' }} />}
                                                sx={{ 
                                                    fontWeight: 600,
                                                    color: variant.stockQuantity <= (variant.lowStockAlert || 0) ? 'error.main' : 'inherit',
                                                    borderColor: variant.stockQuantity <= (variant.lowStockAlert || 0) ? 'error.main' : 'text.border'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <NotificationsActive sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                {variant.lowStockAlert || 0}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={variant.isActive ? <Visibility /> : <VisibilityOff />}
                                                label={variant.isActive ? "Active" : "Inactive"}
                                                size="small"
                                                sx={{
                                                    backgroundColor: variant.isActive ? 'primary.main' : 'grey.400',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    '& .MuiChip-icon': { color: 'white', fontSize: 16 }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, variant)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={variants.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={1}
                PaperProps={{
                    sx: { minWidth: 150, border: '1px solid', borderColor: 'text.border', mt: 1 }
                }}
            >
                <MenuItem onClick={() => {
                    navigate(`/variants/modify/${productId}/${menuSelectedVariant.id}`, { 
                        state: { 
                            variant: menuSelectedVariant,
                            productName,
                            subCategoryName,
                            categoryName,
                            categoryId,
                            subCategoryId
                        } 
                    });
                    handleMenuClose();
                }}>
                    <MuiListItemIcon><Edit fontSize="small" color="primary" /></MuiListItemIcon>
                    Edit Variant
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} disabled>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>
                    Delete Variant
                </MenuItem>
            </Menu>

            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Variant?"
                message={`Are you sure you want to delete "${selectedVariant?.variantLabel}"? This action cannot be undone.`}
            />
        </Box>
    );
};

export default GetAllVarients;
